import { useEffect } from "react";
import { listen } from "@tauri-apps/api/event";
import { openUrl } from "@tauri-apps/plugin-opener";
import { getDesktopDownloadUrl } from "@/lib/desktop-download";
import { translate } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { updatesMessages } from "@/i18n/messages/updates";
import { useNotificationStore } from "@/stores/notification-store";
import { safeTauriUnlisten } from "./tauri-listener-cleanup";

export const MANUAL_DESKTOP_DOWNLOAD_EVENT = "denker://download-desktop-update";
const ERROR_ID = "desktop-manual-download-failed";

/** The owner opens one download even when its toast is on another display. */
export function useDesktopUpdateDownload(role: string) {
  useEffect(() => {
    if (role !== "owner") return;
    let disposed = false;
    let opening = false;
    // One app serves every channel; the small installer it starts replaces
    // a running app in place and keeps its channel.
    const url = getDesktopDownloadUrl();
    const showFailure = (canRetry: boolean) => {
      if (disposed) return;
      useNotificationStore.getState().addSystemToast(() => ({
        id: ERROR_ID,
        appWide: true,
        title: translate(updatesMessages, "openDownloadFailed"),
        description: translate(
          updatesMessages,
          canRetry ? "openAddressRetry" : "openAddress",
          { url },
        ),
        level: "error",
        persistent: true,
        ...(canRetry
          ? {
              actionLabel: translate(commonMessages, "retry"),
              actionEvent: MANUAL_DESKTOP_DOWNLOAD_EVENT,
            }
          : {}),
      }));
    };
    const pending = listen(MANUAL_DESKTOP_DOWNLOAD_EVENT, async () => {
      if (disposed || opening) return;
      opening = true;
      try {
        await openUrl(url);
        if (!disposed)
          useNotificationStore.getState().removeSystemToast(ERROR_ID);
      } catch {
        showFailure(true);
      } finally {
        opening = false;
      }
    }).catch(() => {
      showFailure(false);
      return () => {};
    });
    return () => {
      disposed = true;
      void pending.then(safeTauriUnlisten);
    };
  }, [role]);
}
